import React, { useState } from "react"
import { IoArrowBack, IoCloseOutline } from "react-icons/io5"
import { BottomSheet } from "react-spring-bottom-sheet"
import "react-spring-bottom-sheet/dist/style.css"

import {
  showErrorToast,
  showSuccessToast,
} from "@fuse/sharedComponents/themeToast/toastUtils"

import { useCancelEvent } from "@api/services/events/useEventsApi"

import {
  TDeviceType,
  useDeviceTypeByWidth,
} from "@fuse/customHooks/useDeviceTypeByWidth"

interface CancelPublishedEventModalProps {
  isOpen: boolean
  onClose: () => void
  eventId: string
  eventName: string
  onEventCancelled?: () => void
}

/**
 * Modal for cancelling a published/live event
 * More serious flow with warnings about attendees, refunds, etc.
 */
const CANCELLATION_REASONS = [
  "Schedule conflict",
  "Low attendance",
  "Venue unavailable",
  "Personal emergency",
  "Weather concerns",
  "Technical issues",
  "Other",
]

const CancelPublishedEventModal: React.FC<CancelPublishedEventModalProps> = ({
  isOpen,
  onClose,
  eventId,
  eventName,
  onEventCancelled,
}) => {
  const { mutate: cancelEvent, isLoading } = useCancelEvent()
  const deviceType = useDeviceTypeByWidth()
  const isMobileDevice = deviceType === TDeviceType.MOBILE

  // Modal state
  const [step, setStep] = useState<"confirm" | "reason">("confirm")
  const [selectedReason, setSelectedReason] = useState<string>("")
  const [additionalDetails, setAdditionalDetails] = useState<string>("")

  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      handleClose()
    }
  }

  const handleClose = () => {
    // Reset modal state when closing
    setStep("confirm")
    setSelectedReason("")
    setAdditionalDetails("")
    onClose()
  }

  const handleConfirmCancel = () => {
    setStep("reason")
  }

  const handleReasonSelect = (reason: string) => {
    setSelectedReason(reason)
  }

  const handleCancelEvent = () => {
    const payload: any = {
      eventId,
      closeReason: selectedReason,
    }

    // Add additional details if provided
    if (additionalDetails.trim()) {
      payload.otherReasonToClose = additionalDetails.trim()
    }

    cancelEvent(payload, {
      onSuccess: (data) => {
        showSuccessToast("Event cancelled successfully")
        onEventCancelled?.()
        handleClose()
      },
      onError: (error: any) => {
        console.error("Error cancelling event:", error)
        showErrorToast(error?.message || "Failed to cancel event")
      },
    })
  }

  const renderConfirmationStep = () => (
    <div className="bg-[#252525B2] backdrop-blur-xl rounded-2xl sm:rounded-2xl w-full">
      {/* Header - Fixed with Navigation */}
      <div className="flex items-center justify-center relative p-6 pb-4">
        <h2 className="text-white text-lg font-semibold text-center">
          Cancel Event
        </h2>
        <div className="absolute right-0 cursor-pointer" onClick={handleClose}>
          <IoCloseOutline color="#fff" size={22} />
        </div>
      </div>

      <div className="px-8 pb-8 text-center">
        {/* Main Question */}
        <h3 className="text-white text-xl font-medium mb-4">
          Are you sure you want to cancel this event?
        </h3>

        {/* Warning */}
        <p className="text-white/70 text-base mb-8 leading-relaxed">
          Keep in mind, once you cancel it, you won't be able to publish it
          again.
        </p>
      </div>

      {/* Buttons */}
      <div className="border-t border-white/10 rounded-b-2xl">
        {/* Proceed to Reason Button */}
        <button
          className="w-full py-4 text-red-400 font-medium border-b border-white/10 hover:bg-white/10 transition-colors disabled:opacity-50"
          onClick={handleConfirmCancel}
          disabled={isLoading}
        >
          Yes, Cancel Event
        </button>

        {/* Keep Event Button */}
        <button
          className="w-full py-4 text-blue-400 font-medium hover:bg-white/10 transition-colors rounded-b-2xl"
          onClick={handleClose}
          disabled={isLoading}
        >
          Don't Cancel My Event
        </button>
      </div>
    </div>
  )

  const renderReasonStep = () => (
    <div className="bg-[#252525B2] backdrop-blur-xl rounded-2xl sm:rounded-2xl w-full max-h-[85vh] flex flex-col">
      {/* Header - Fixed with Navigation */}
      <div className="flex items-center justify-center relative p-6 pb-4">
        <div
          className="absolute left-0 cursor-pointer"
          onClick={() => setStep("confirm")}
        >
          <IoArrowBack color="#fff" size={20} />
        </div>
        <h2 className="text-white text-lg font-semibold text-center">
          Why are you cancelling?
        </h2>
        <div className="absolute right-0 cursor-pointer" onClick={handleClose}>
          <IoCloseOutline color="#fff" size={22} />
        </div>
      </div>

      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto px-6 pb-2">
        {/* Reason Options */}
        <div className="space-y-2 mb-6">
          {CANCELLATION_REASONS.map((reason) => (
            <button
              key={reason}
              className={`w-full py-3 px-4 text-left rounded-lg border transition-colors text-sm ${
                selectedReason === reason
                  ? "bg-blue-600/20 border-blue-400 text-blue-300"
                  : "bg-white/5 border-white/10 text-white hover:bg-white/10"
              }`}
              onClick={() => handleReasonSelect(reason)}
            >
              {reason}
            </button>
          ))}
        </div>
      </div>

      {/* Confirm Button - Fixed at Bottom */}
      <div className="border-t border-white/10 p-6 pt-4">
        {/* Additional Details  */}
        <div className="mb-6">
          <label className="block text-white text-base mb-3 font-small">
            Additional details
          </label>
          <textarea
            className="w-full p-4 bg-white/10 border border-white/30 rounded-xl text-white placeholder-white/60 resize-none text-base leading-relaxed focus:border-blue-400 focus:outline-none focus:bg-white/15 transition-all"
            placeholder="e.g., venue cancelled last minute, weather forecast looks bad, got sick..."
            value={additionalDetails}
            onChange={(e) => setAdditionalDetails(e.target.value)}
            rows={4}
          />
        </div>
        <button
          className={`w-full py-4 font-medium rounded-lg transition-all ${
            isLoading || !selectedReason
              ? "bg-gray-500/50 text-white/50 cursor-not-allowed"
              : additionalDetails.trim()
                ? "bg-red-500/80 hover:bg-red-500 text-white"
                : "bg-red-500/40 hover:bg-red-500/60 text-white/80 hover:text-white"
          }`}
          onClick={handleCancelEvent}
          disabled={isLoading || !selectedReason}
        >
          {isLoading ? "Cancelling..." : "Confirm Cancellation"}
        </button>
      </div>
    </div>
  )

  const renderModalContent = () => {
    return step === "confirm" ? renderConfirmationStep() : renderReasonStep()
  }

  if (!isOpen) return null

  return isMobileDevice ? (
    <BottomSheet
      open={true}
      onDismiss={handleClose}
      className="rsbs-theme-dark rounded-2xl"
    >
      <div className="p-4">{renderModalContent()}</div>
    </BottomSheet>
  ) : (
    <div
      className="fixed inset-0 h-full flex items-center justify-center bg-black bg-opacity-50 z-[100]"
      onClick={handleOverlayClick}
    >
      <div
        className="max-w-sm mx-4 relative"
        onClick={(e) => e.stopPropagation()}
      >
        {renderModalContent()}
      </div>
    </div>
  )
}

export default CancelPublishedEventModal
